import React, { ChangeEvent, Fragment, useEffect, useState } from "react";
import { useGetAllCategoryListQuery } from "../../../utils/api/category/category.api";
import { usePagination } from "../../../utils/hooks";
import Accordion from "react-bootstrap/Accordion";
import { AlertInfo } from "../../common/Alert";
import { sortByAlphabetical, getImage } from "../../../utils/Utils";
import { Color } from "../../../utils/theme";
import { useGetProductListQuery } from "../../../utils/api/product/product.api";
import { ApiBaseUrl } from "../../../utils/http";

// Le champ `images` d'une sous-catégorie est un chemin déjà préfixé par
// "/mediafiles/..." (contrairement aux champs images des produits, qui eux
// passent par getImage() avec son préfixe "/api"). On construit donc
// directement l'URL ici, sans passer par getImage().
function getSubCategoryImageUrl(path?: string | null) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${ApiBaseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
}

// Affiche une ligne de sous-catégorie : vignette (image propre à la sous-catégorie,
// ou à défaut la photo de son premier produit), rond façon radio, nom.
function SubCategoryItem({ sub, category, isChecked, onChange }) {
  // On ne va chercher un produit de secours que si la sous-catégorie n'a pas
  // sa propre image (évite un appel réseau inutile).
  const { data: fallbackData } = useGetProductListQuery(
    {
      page: 1,
      limit: 1,
      categorie: category?.slug,
      sous_categorie: [sub?.slug] as any,
    },
    { skip: !!sub?.images }
  );

  const fallbackProduct = fallbackData?.results?.[0];
  const fallbackProductImage = fallbackProduct
    ? fallbackProduct?.type === "taille_unique"
      ? fallbackProduct?.images?.[0]?.image
      : fallbackProduct?.variations?.[0]?.images?.[0]?.image
    : null;

  const rawImagePath = sub?.images ? getSubCategoryImageUrl(sub.images) : null;
  const imageUrl =
    rawImagePath ||
    (fallbackProductImage ? getImage(fallbackProductImage) : null);

  return (
    <div
      className={`subcategory-item${
        isChecked ? " subcategory-item-active" : ""
      }`}
    >
      <label className="subcategory-label">
        {/*
          FIX : on utilise réellement un input de type "radio" (au lieu de
          "checkbox") et on donne à tous les inputs d'une même catégorie le
          même `name`. Le navigateur garantit alors nativement qu'un seul
          bouton peut être sélectionné à la fois pour ce groupe.
        */}
        <input
          type="radio"
          name={`sous_categorie_${category?.slug}`}
          className="subcategory-radio-input"
          checked={!!isChecked}
          value={sub?.slug}
          onChange={onChange}
        />
        <span
          className="subcategory-radio-visual"
          style={isChecked ? { borderColor: Color.themeColor } : {}}
        >
          {isChecked && (
            <span
              className="subcategory-radio-dot"
              style={{ backgroundColor: Color.themeColor }}
            />
          )}
        </span>

        {imageUrl && (
          <img
            src={imageUrl}
            alt={sub?.nom}
            className="subcategory-thumbnail"
            onError={(e) => {
              // Évite l'icône d'image cassée si l'URL est invalide
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        )}

        <span
          className="subcategory-name"
          style={isChecked ? { color: Color.themeColor } : {}}
        >
          {sub?.nom}
        </span>
      </label>
    </div>
  );
}
function safeParseSousCategorie(value: string | null) {
  if (!value) return [];
  try {
    return JSON.parse(value);
  } catch {
    return [value];
  }
}
function FilterItems({
  searchParams,
  setSearchParams,
  query,
  setQuery,
  setPage,
}) {
  
  const [selectedCat, setSelectedCat] = React.useState<any>(
    searchParams?.get("sous_categorie")
      ?safeParseSousCategorie(searchParams?.get("sous_categorie"))
      : []
  );
  const [parent, setParent] = React.useState<any>(
    searchParams?.get("categorie") || ""
  );
  const [value, setValue] = React.useState<any>({
    min: searchParams?.get("prix_min") || "",
    max: searchParams?.get("prix_max") || "",
  });
  const {
    data = { results: [] },
    isLoading,
    refetch,
  } = useGetAllCategoryListQuery();

  const [activeKey, setActiveKey] = useState(null);
  // Ne jamais réécrire l'URL au montage : ça peut écraser "/" dans l'historique.
  const canSyncUrlRef = React.useRef(false);

  const handleToggle = (key) => {
    setActiveKey(key);
  };

  const syncUrlFromQuery = React.useCallback(
    (nextQuery: Record<string, unknown>) => {
      if (!canSyncUrlRef.current) return;
      const params = new URLSearchParams();
      Object.entries(nextQuery || {}).forEach(([key, val]) => {
        if (val !== undefined && val !== null && String(val) !== "") {
          params.set(key, String(val));
        }
      });
      if (params.toString() !== searchParams.toString()) {
        setSearchParams(params, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  React.useEffect(() => {
    const nextSousCat = selectedCat?.length ? JSON.stringify(selectedCat) : "";
    if (query?.sous_categorie === nextSousCat) return;
    const nextQuery = {
      ...query,
      sous_categorie: nextSousCat,
    };
    setQuery(nextQuery);
    syncUrlFromQuery(nextQuery);
  }, [selectedCat]);

  const handleFilter = () => {
    const nextMin = value?.min ?? "";
    const nextMax = value?.max ?? "";
    if (query?.prix_min === nextMin && query?.prix_max === nextMax) return;

    setPage(1);
    const nextQuery = {
      ...query,
      prix_min: nextMin,
      prix_max: nextMax,
    };
    setQuery(nextQuery);
    syncUrlFromQuery(nextQuery);
  };

  useEffect(() => {
    // Ignorer le premier passage (montage) pour préserver l'historique
    if (!canSyncUrlRef.current) return;
    handleFilter();
  }, [value]);

  const handleChangeParent = (item) => {
    canSyncUrlRef.current = true;
    setParent(item?.slug);
    let queryCopy = { ...query };
    queryCopy = {
      ...queryCopy,
      categorie: item?.slug,
      sous_categorie: [],
    };
    setSelectedCat([]);
    setQuery(queryCopy);
    syncUrlFromQuery(queryCopy);
  };

  // FIX : au lieu d'empiler (push) le slug coché dans le tableau existant
  // -> ce qui permettait à plusieurs "radios" d'être actifs en même temps -,
  // on REMPLACE la sélection par le seul slug choisi. Un clic sur une
  // sous-catégorie désélectionne donc automatiquement la précédente.
  const handleSelectedCat = (e: ChangeEvent<HTMLInputElement>, item) => {
    canSyncUrlRef.current = true;
    if (item?.slug !== parent) {
      handleChangeParent(item);
    }

    if (e?.target?.checked) {
      setSelectedCat([e.target.value]);
    } else {
      setSelectedCat([]);
    }

    setPage(1);
  };

  const handleClick = (item) => {
    canSyncUrlRef.current = true;
    handleToggle(item?.id);
    setParent(item?.slug);
    let queryCopy = { ...query };
    queryCopy = {
      ...queryCopy,
      categorie: item?.slug,
      sous_categorie: [],
    };
    setSelectedCat([]);
    setQuery(queryCopy);
    syncUrlFromQuery(queryCopy);
    setPage(1);
  };

  const handlePriceChange = (nextValue: { min: string | number; max: string | number }) => {
    canSyncUrlRef.current = true;
    setValue(nextValue);
  };

  return (
    <div className="col-lg-3 col-md-5 col-left-achat-page">
      <div
        className="content-col-left-achat-page"
        style={{ minHeight: "70rem" }}
      >
        <div className="content-others-value my-4 pb-3">
          <p className="content-title-section-left pb-2">Catégories</p>

          <div className="categories_and_subcategories">
            {!!isLoading && <p>Loading...</p>}

            <Accordion
              className="category-filter-acordion"
              id="accordionCategories"
              alwaysOpen
              defaultActiveKey={["0"]}
            >
              {!isLoading &&
                (sortByAlphabetical(data?.results)?.length ? (
                  sortByAlphabetical(data?.results)?.map((category, i) => (
                    <Accordion.Item
                      eventKey={`${i}`}
                      key={category?.id}
                      className="category-filter-acordion-item mb-1"
                    >
                      <Accordion.Header
                        className="mb-0"
                        onClick={() => handleClick(category)}
                      >
                        <span
                          className="category_parent_name"
                          style={{
                            color:
                              category?.id == activeKey
                                ? Color.themeColor
                                : "#000000b3",
                          }}
                        >
                          {category?.nom}
                        </span>
                      </Accordion.Header>
                      <Accordion.Body>
                        {category?.sous_categorie?.length
                          ? sortByAlphabetical(category?.sous_categorie)?.map(
                              (sub) => {
                                const isChecked = !!selectedCat?.includes(sub?.slug);

                                return (
                                  <SubCategoryItem
                                    key={sub?.id}
                                    sub={sub}
                                    category={category}
                                    isChecked={isChecked}
                                    onChange={(e) =>
                                      handleSelectedCat(e, category)
                                    }
                                  />
                                );
                              }
                            )
                          : null}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))
                ) : (
                  <AlertInfo message="Aucune catégorie disponible pour le moment" />
                ))}
            </Accordion>
          </div>
        </div>
        <p className="content-title-section-left">
          Prix <span style={{ fontWeight: 300, fontSize: 14 }}>(F CFA)</span>
        </p>
        <div className="pb-4 py-4">
          <label className="material-checkbox mb-3" key="10">
           <input
  type="radio"
  name="prix"
  checked={value.min === "" && value.max === ""}
  onChange={() => handlePriceChange({ min: "", max: "" })}
/>
            <span>Tout</span>
          </label>
          <label className="material-checkbox mb-3" key="10">
            <input
              type="radio"
              value={10}
              name="prix"
              checked={parseInt(value?.max) === 10000}
              onChange={() => handlePriceChange({ min: 0, max: 10000 })}
            />
            <span>Jusqu'a 10 000</span>
          </label>
          <label className="material-checkbox mb-3" key="25">
            <input
              type="radio"
              value={25}
              name="prix"
              checked={parseInt(value?.max) === 25000}
              onChange={() => handlePriceChange({ min: 10000, max: 25000 })}
            />
            <span>De 10 000 à 25 000</span>
          </label>
          <label className="material-checkbox mb-3" key="50">
            <input
              type="radio"
              value={50}
              name="prix"
              checked={parseInt(value?.max) === 50000}
              onChange={() => handlePriceChange({ min: 25000, max: 50000 })}
            />
            <span>De 25 000 à 50 000</span>
          </label>
          <label className="material-checkbox mb-3" key="60">
            <input
              className="radio-input"
              type="radio"
              value={60}
              name="prix"
              checked={parseInt(value?.max) === 90000000000}
              onChange={() => handlePriceChange({ min: 50000, max: 90000000000 })}
            />
            <span>De 50 000 et plus</span>
          </label>
        </div>
      </div>
    </div>
  );
}

export default FilterItems;